import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  FoldVerticalIcon,
  UnfoldVerticalIcon,
} from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import type { NodeTypeIconChange } from "@/components/organisms/Node/NodeTitleLayoutEntityBox";
import { ToolCallCardNodeLayout } from "@/components/organisms/ToolCallCard/ToolCallCardNodeLayout";
import { ToolCallCardPendingStatus } from "@/components/organisms/ToolCallCard/ToolCallCardPendingStatus";
import { EnumColors } from "@/constants/values";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

import { PendingNode } from "./PendingSession.types";
import { cn } from "@/components/ui/utils";

type PendingChangesPanelProps = {
  pendingNodes: PendingNode[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onConfirmAll: () => void;
  onRejectAll: () => void;
  confirmingSession: boolean;
  rejectingSession: boolean;
  /** The business whose chat the tray is in: a record of its own needs no badge. */
  entityIdentifier?: string | null;
  /** Opened to review its changes: each is laid out open, to put right before it is confirmed. */
  isReviewing?: boolean;
};

// ── Change types ──────────────────────────────────────────────────────────────

/** Each kind of change, as its record's mark says it: its colour, and its word on hover. */
const CHANGE_TYPE_CONFIG: Record<
  string,
  { label: string; enumColor: NodeTypeIconChange["enumColor"] }
> = {
  create: { label: "created", enumColor: EnumColors.GREEN },
  delete: { label: "deleted", enumColor: EnumColors.RED },
  update: { label: "updated", enumColor: EnumColors.ORANGE },
};

// ── Object grouping ──────────────────────────────────────────────────────────

type GroupedObject = {
  objectIdentifier: string;
  displayName: string;
  /** Its record as the change list read it: its title fields and business. */
  node: PendingNode["node"];
  modelName: string;
  typename: string;
  changes: PendingNode[];
  summaryChangeType: string;
  allResolved: boolean;
};

function groupByObject(nodes: PendingNode[]): GroupedObject[] {
  const groups = new Map<string, GroupedObject>();

  for (const node of nodes) {
    const objId = node.node.identifier;
    const existing = groups.get(objId);

    if (existing) {
      existing.changes.push(node);
      existing.displayName =
        node.node.name || node.node.description || existing.displayName;
      existing.node = { ...existing.node, ...node.node };
      if (node.changeType === "create") {
        existing.summaryChangeType = "create";
      } else if (
        node.changeType === "delete" &&
        existing.summaryChangeType !== "create"
      ) {
        existing.summaryChangeType = "delete";
      }
    } else {
      groups.set(objId, {
        objectIdentifier: objId,
        displayName:
          node.node.name ||
          node.node.description ||
          node.node.identifier?.slice(0, 8) ||
          "Unnamed",
        node: node.node,
        modelName: node.modelName || "item",
        typename: node.node.__typename || "Object",
        changes: [node],
        summaryChangeType: node.changeType || "update",
        allResolved: false,
      });
    }
  }

  for (const group of groups.values()) {
    group.allResolved = group.changes.every(
      (c) => c.status === "confirmed" || c.status === "rejected",
    );
  }

  return Array.from(groups.values());
}

// ── Object row (accordion) ───────────────────────────────────────────────────

function EndDecorator({ group }: { group: GroupedObject }) {
  return (
    <div className="flex items-center gap-2">
      {group.changes.length > 1 && (
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 text-[10px] shrink-0"
        >
          {group.changes.length} changes
        </Typography>
      )}
      {/* A turn is not all or nothing: a contact the agent made on its way to
          a document can go without the document going with it. The same
          Reject / Confirm the tool call card carries, deciding this record
          alone - every change it has, since rejecting the last edit would
          leave the record that edit was made to. */}
      {!group.allResolved && (
        <div data-testid="pending-object-row-actions" className="flex">
          <ToolCallCardPendingStatus
            node={undefined}
            hideDecision
            // Listed here, it awaits review: nothing to read to offer it.
            isUndecided
            pendingChangeType={group.summaryChangeType}
            scopedChangeIdentifiers={group.changes
              .map((change) => change.changeIdentifier)
              .filter((identifier): identifier is string =>
                Boolean(identifier),
              )}
          />
        </div>
      )}
    </div>
  );
}

function ObjectRow({
  group,
  isExpanded,
  entityIdentifier,
}: {
  group: GroupedObject;
  isExpanded?: boolean;
  entityIdentifier?: string | null;
}) {
  const [expanded, setExpanded] = useState(isExpanded ?? false);
  // Whether it has come into sight in the tray's list, scrolled to or not -
  // one in sight as the tray opens before it is first drawn. Opened rows read
  // their records only then: every change opened to review, out of sight too,
  // was fifteen hundred reads at once.
  const rowRef = useRef<HTMLDivElement>(null);
  const [isInSight, setIsInSight] = useState(false);
  useLayoutEffect(() => {
    const row = rowRef.current?.getBoundingClientRect();
    const list = rowRef.current
      ?.closest("[data-testid='pending-changes-list']")
      ?.getBoundingClientRect();
    if (row && (!list || (row.top < list.bottom && row.bottom > list.top))) {
      setIsInSight(true);
    }
  }, []);
  useEffect(() => {
    const row = rowRef.current;
    if (!row || isInSight) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) setIsInSight(true);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [isInSight]);
  const isOpen = expanded && isInSight;
  // What it did - or, decided, where it stands - said on its record's mark.
  const confirmed = group.changes.every(
    (change) => change.status === "confirmed",
  );
  const typeIconChange: NodeTypeIconChange = group.allResolved
    ? {
        change: confirmed ? "confirmed" : "resolved",
        label: confirmed ? "confirmed" : "resolved",
        enumColor: EnumColors.GREY_1,
      }
    : {
        change: group.summaryChangeType,
        ...(CHANGE_TYPE_CONFIG[group.summaryChangeType] ??
          CHANGE_TYPE_CONFIG.update),
      };

  return (
    <div
      ref={rowRef}
      data-testid={`pending-object-row-${group.objectIdentifier}`}
      className={cn(
        group.allResolved ? "opacity-55" : "opacity-100",
        // Its Reject and Confirm on the row under the pointer or the keyboard -
        // out of sight on the rest, opened or not, so a list of them reads as
        // what changed.
        "[&_[data-testid='pending-object-row-actions']]:opacity-0 [&_[data-testid='pending-object-row-actions']]:[transition:opacity_120ms_ease]",
        "[&:hover_[data-testid='pending-object-row-actions']]:opacity-100 [&:focus-within_[data-testid='pending-object-row-actions']]:opacity-100",
        "[@media(hover:none)]:[&_[data-testid='pending-object-row-actions']]:opacity-100",
        // ``clip`` keeps the row trimmed without becoming a scroll container,
        // which would trap the card's sticky title inside this row.
        "[overflow:clip]",
        // A title draws the line under itself; opened, the row's last field
        // needs one too, before the next record's title.
        isOpen &&
          "[&:not(:last-child)]:[border-bottom-width:1px] [&:not(:last-child)]:[border-bottom-style:solid] [&:not(:last-child)]:border-border",
      )}
    >
      {/* Opened, a row is the change rather than the record: the fields it
          moved, or for a new record the ones it set. The latest change carries
          the record's current deltas; a record created here reads as created
          however many edits followed. */}
      <ToolCallCardNodeLayout
        item={{
          ...group.node,
          identifier: group.objectIdentifier,
          __typename: group.typename,
          name: group.displayName,
        }}
        // Its change read with its record, once it is opened in sight.
        pendingChangeIdentifier={
          isOpen
            ? group.changes[group.changes.length - 1].changeIdentifier
            : undefined
        }
        pendingChangeType={group.summaryChangeType}
        size="sm"
        // A list of mixed records: what each one is, at a glance - and only
        // that: the record page's tools, and the badge of the business the
        // chat is in, say nothing about the change.
        isTypeIconVisible
        typeIconChange={typeIconChange}
        hideRecordTools
        contextEntityIdentifier={entityIdentifier}
        hideBody={!isOpen}
        // Named, badged and offered for review from the session's one read of
        // its changes; its record read only once it is opened. A statement read
        // in a chat drafts a row for every line on it, and each row in sight
        // reading its own record and change held the database up.
        loadOnOpen
        onTitleClick={() => setExpanded((v) => !v)}
        titleEndDecorator={<EndDecorator group={group} />}
      />
    </div>
  );
}

/** The records a set of changes touched, one openable row each. */
export function PendingChangesList({
  pendingNodes,
  areAllExpanded,
  entityIdentifier,
}: {
  pendingNodes: PendingNode[];
  /** Open or close every row at once. */
  areAllExpanded?: boolean;
  /** The business whose chat they are in: a record of its own needs no badge. */
  entityIdentifier?: string | null;
}) {
  return groupByObject(pendingNodes).map((group) => (
    <ObjectRow
      // Expanding or collapsing them all starts every row over in that state;
      // each still opens and closes on its own after.
      key={`${group.objectIdentifier}-${areAllExpanded}`}
      group={group}
      isExpanded={areAllExpanded}
      entityIdentifier={entityIdentifier}
    />
  ));
}

// ── Main panel ───────────────────────────────────────────────────────────────

export function PendingChangesPanel({
  pendingNodes,
  expanded,
  onToggleExpanded,
  onConfirmAll,
  onRejectAll,
  confirmingSession,
  rejectingSession,
  entityIdentifier = null,
  isReviewing = false,
}: PendingChangesPanelProps) {
  const pendingCount = pendingNodes.filter(
    (n) => n.status === "pending",
  ).length;
  const [areAllExpanded, setAreAllExpanded] = useState(isReviewing);
  // Opened to review: every change laid out open.
  useEffect(() => {
    if (isReviewing) setAreAllExpanded(true);
  }, [isReviewing]);
  // One record is its own row and nothing more: a strip over it would only
  // say its count and repeat its Reject and Confirm.
  const isOneRecord =
    new Set(pendingNodes.map((node) => node.node.identifier)).size === 1;

  // The tray at the top of the composer card (``ThreadBox``'s ``header``): a
  // strip with the count and the session-wide actions that opens onto the
  // changed records. The card draws the outline; the tray draws its own top
  // corners (the card does not clip, so its poppers are not cut off) and the
  // divider between it and the composer.
  return (
    <AnimatePresence initial={false}>
      {pendingNodes.length > 0 && (
        <motion.div
          key="pending-changes-panel"
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{ overflow: "hidden", width: "100%" }}
        >
          <div
            data-testid="pending-changes-panel"
            className="w-full text-left bg-muted rounded-tl-[calc(var(--radius)_+_4px_-_1px)] rounded-tr-[calc(var(--radius)_+_4px_-_1px)] [border-bottom-width:1px] [border-bottom-style:solid] border-border"
          >
            {isOneRecord ? (
              // The row heads the card: cut to its rounded corners, as the
              // strip over several is, rather than drawn square past them.
              <div
                data-testid="pending-changes-list"
                className="max-h-70 overflow-y-auto rounded-tl-[calc(var(--radius)_+_4px_-_1px)] rounded-tr-[calc(var(--radius)_+_4px_-_1px)]"
              >
                <PendingChangesList
                  pendingNodes={pendingNodes}
                  areAllExpanded={areAllExpanded}
                  entityIdentifier={entityIdentifier}
                />
              </div>
            ) : (
              <>
                {/* Strip */}
                <div
                  onClick={onToggleExpanded}
                  className="flex items-center justify-between gap-4 min-h-9 pl-3 pr-3 pt-0.5 pb-0.5 cursor-pointer"
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Typography
                      level="body-sm"
                      className="truncate text-muted-foreground font-semibold"
                    >
                      {pendingCount > 0
                        ? `${pendingCount} pending change${pendingCount !== 1 ? "s" : ""}`
                        : `${pendingNodes.length} change${pendingNodes.length !== 1 ? "s" : ""} (all resolved)`}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {pendingCount > 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRejectAll();
                          }}
                          disabled={rejectingSession}
                        >
                          {rejectingSession && <Spinner />}Reject all
                        </Button>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfirmAll();
                          }}
                          disabled={confirmingSession}
                        >
                          {confirmingSession && <Spinner />}Confirm all
                        </Button>
                      </>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={
                            areAllExpanded
                              ? "Collapse all changes"
                              : "Expand all changes"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = !areAllExpanded;
                            setAreAllExpanded(next);
                            // Opened rows are no use in a closed tray.
                            if (next && !expanded) onToggleExpanded();
                          }}
                        >
                          <Icon
                            ReactIcon={
                              areAllExpanded
                                ? FoldVerticalIcon
                                : UnfoldVerticalIcon
                            }
                            size="sm"
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {areAllExpanded
                          ? "Collapse every change back to its title"
                          : "Expand every change to review what it sets before confirming"}
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={expanded ? "Hide changes" : "Show changes"}
                      aria-expanded={expanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpanded();
                      }}
                    >
                      <Icon
                        ReactIcon={expanded ? ChevronUpIcon : ChevronDownIcon}
                        size="sm"
                      />
                    </Button>
                  </div>
                </div>

                {/* The changed records, inside the tray */}
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        data-testid="pending-changes-list"
                        className="max-h-70 overflow-y-auto [border-top-width:1px] [border-top-style:solid] border-border"
                      >
                        <PendingChangesList
                          pendingNodes={pendingNodes}
                          areAllExpanded={areAllExpanded}
                          entityIdentifier={entityIdentifier}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
